/**
 * SAB-001 — CANONICAL FINANCIAL SURFACE BINDING.
 *
 * Presentation-only helpers for surfaces OUTSIDE the certified report chain
 * (Operations console, customer Negotiation workspace, notifications, emails).
 *
 * These helpers contain NO financial derivation. They only:
 *   • format a canonical value that was resolved elsewhere
 *     (`resolveCanonicalRecovery` via `useRecoveryFacts` / `ReportSummary`), or
 *   • compute ACTUAL settlement math from two recorded facts
 *     (original amount and the recorded settlement amount).
 *
 * No surface may estimate recovery from an invoice total using a percentage.
 */

import { currency } from "@/lib/report";

/* ───────────────────────── Fallback vocabulary ───────────────────────── */

/** A canonical value exists in principle but has not been produced yet. */
export const NOT_YET_CALCULATED = "Not yet calculated";
/** No canonical value is available for this surface. */
export const NOT_AVAILABLE = "Not available";
/** Non-live sample/demo record — never show fictional dollars. */
export const DEMO_NO_FINANCIALS = "Sample record — no live financial result";

/* ───────────────────────── Frozen labels ───────────────────────── */

export const SURFACE_LABELS = {
  invoiceTotal: "Invoice Total",
  supportedRecovery: "Total Supported Recovery",
  settlementGoal: "Settlement Goal",
  actualNegotiatedSavings: "Actual Negotiated Savings",
  actualSavingsPct: "Actual Savings %",
  finalSettlementAmount: "Final Settlement Amount",
} as const;

/* ───────────────────────── Formatters ───────────────────────── */

function isMoney(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n);
}

/**
 * Display for a canonical Total Supported Recovery. `null`/undefined is never
 * rendered as `$0` and never replaced with a percentage estimate.
 */
export function supportedRecoveryDisplay(recovery: number | null | undefined): string {
  return isMoney(recovery) ? currency(recovery) : NOT_YET_CALCULATED;
}

/** Display for any canonical money value that may be missing. */
export function canonicalMoneyDisplay(value: number | null | undefined): string {
  return isMoney(value) && value > 0 ? currency(value) : NOT_AVAILABLE;
}

/* ───────────────────────── Actual settlement math ───────────────────────── */

/**
 * ACTUAL negotiated savings — only valid once a settlement amount is recorded.
 * This is a recorded outcome, never pre-negotiation supported recovery.
 */
export function actualNegotiatedSavings(
  originalAmount: number | null | undefined,
  settlementAmount: number | null | undefined,
): number | null {
  if (!isMoney(originalAmount) || !isMoney(settlementAmount)) return null;
  return Math.round((originalAmount - settlementAmount) * 100) / 100;
}

/** ACTUAL savings percentage of the original amount. */
export function actualSavingsPct(
  originalAmount: number | null | undefined,
  settlementAmount: number | null | undefined,
): number | null {
  const savings = actualNegotiatedSavings(originalAmount, settlementAmount);
  if (savings === null || !isMoney(originalAmount) || originalAmount <= 0) return null;
  return Math.round((savings / originalAmount) * 100);
}

/* ───────────────────────── Surface resolution ───────────────────────── */

export type NegotiationFinancialKind =
  | "actual_settlement"
  | "supported_recovery"
  | "unavailable";

export interface NegotiationFinancialDisplay {
  kind: NegotiationFinancialKind;
  label: string;
  value: string;
  /** Numeric value when known — for sorting/filtering only. */
  amount: number | null;
}

/**
 * The single resolution rule for every negotiation surface (staff + customer):
 *   1. A recorded settlement → ACTUAL negotiated savings.
 *   2. Otherwise a linked review's canonical Total Supported Recovery.
 *   3. Otherwise unavailable — never a percentage-derived estimate.
 */
export function resolveNegotiationFinancial(input: {
  originalAmount?: number | null;
  settlementAmount?: number | null;
  canonicalRecovery?: number | null;
}): NegotiationFinancialDisplay {
  const actual = actualNegotiatedSavings(input.originalAmount, input.settlementAmount);
  if (actual !== null) {
    return {
      kind: "actual_settlement",
      label: SURFACE_LABELS.actualNegotiatedSavings,
      value: currency(actual),
      amount: actual,
    };
  }
  if (isMoney(input.canonicalRecovery)) {
    return {
      kind: "supported_recovery",
      label: SURFACE_LABELS.supportedRecovery,
      value: currency(input.canonicalRecovery),
      amount: input.canonicalRecovery,
    };
  }
  return {
    kind: "unavailable",
    label: SURFACE_LABELS.supportedRecovery,
    value: NOT_AVAILABLE,
    amount: null,
  };
}
